// src/components/forms/CitizenRegistrationForm.js
import React, { useMemo, useRef, useState } from 'react';
import imageCompression from 'browser-image-compression';
import Webcam from 'react-webcam';
import { useNavigate } from 'react-router-dom';

// Tenant-safe API + navigation (keeps /t/:slug/... when in path-mode)
import http from '../../services/http';
import { tenantUrl } from '../../utils/tenantUrl';

const CENTRE_OPTIONS = [
  'CRHA Keru',
  'CRHA Dhawa',
  'CRHA Dhundhara',
  'UHTC Pratap Nagar',
];

const AGE_INPUT_MODES = [
  ['dob', 'Date of birth available'],
  ['reported', 'Age reported / estimated'],
  ['unknown', 'Age not known'],
];

const AGE_UNIT_OPTIONS = [
  ['days', 'Days'],
  ['weeks', 'Weeks'],
  ['months', 'Months'],
  ['years', 'Years'],
];

const AGE_SOURCE_OPTIONS = [
  ['self', 'Citizen / self'],
  ['family', 'Family member'],
  ['caregiver', 'Caregiver'],
  ['health_worker', 'Health worker'],
  ['record', 'Available record'],
  ['other', 'Other source'],
];

const REGISTRATION_SOURCE_OPTIONS = [
  ['cmfm_direct', 'Direct CMFM registration'],
  ['state_hmis', 'Linked from State HMIS'],
];

const todayDateInput = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const createInitialForm = () => ({
  name: '',

  // Age / DOB capture. `age` is retained as a compatibility alias and is
  // generated from ageValue during submission when reported age is used.
  ageInputMode: 'dob',
  dob: '',
  dobStatus: 'reported',
  ageValue: '',
  ageUnit: 'years',
  ageAsOnDate: todayDateInput(),
  ageSource: 'self',
  ageIsApproximate: false,

  gender: '',
  mobile: '',
  email: '',
  address: '',
  aadhaar: '',
  occupation: '',
  centre: '',
  preferredLanguage: '',

  tobaccoSmoked: 'Never',
  tobaccoSmokeless: 'Never',
  alcoholUse: 'Never',
  presentingComplaint: '',

  // Patient-level State HMIS linkage only. HMIS token, encounter ID and
  // referral details belong to AddVisit/Visit rather than Citizen.
  registrationSource: 'cmfm_direct',
  externalSystem: '',
  externalPatientId: '',
  externalRegistrationNumber: '',

  photo: null,
  password: '',
});

function normalizeCentre(value) {
  const original = String(value || '').trim();
  if (!original) return '';

  const key = original
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (key.includes('keru')) return 'CRHA Keru';
  if (key.includes('dhawa')) return 'CRHA Dhawa';
  if (key.includes('pratap')) return 'UHTC Pratap Nagar';
  if (
    ['dundhara', 'dhundhara', 'dundara', 'dhundara'].some((item) =>
      key.includes(item)
    )
  ) {
    return 'CRHA Dhundhara';
  }

  return original;
}

function numericValue(value) {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseDateInput(value) {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function calculateAgeFromDob(dobValue, asOnValue = todayDateInput()) {
  const dob = parseDateInput(dobValue);
  const asOn = parseDateInput(asOnValue) || new Date();
  if (!dob || dob.getTime() > asOn.getTime()) return null;

  const startUtc = Date.UTC(dob.getFullYear(), dob.getMonth(), dob.getDate());
  const endUtc = Date.UTC(asOn.getFullYear(), asOn.getMonth(), asOn.getDate());
  const days = Math.floor((endUtc - startUtc) / 86400000);

  let completedYears = asOn.getFullYear() - dob.getFullYear();
  const monthDifference = asOn.getMonth() - dob.getMonth();
  const dayDifference = asOn.getDate() - dob.getDate();
  if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
    completedYears -= 1;
  }

  if (days <= 59) {
    return {
      days,
      completedYears: Math.max(0, completedYears),
      display: `${days} day${days === 1 ? '' : 's'}`,
    };
  }

  if (days < 365.2425) {
    const months = Math.max(2, Math.floor(days / 30.4375));
    return {
      days,
      completedYears: 0,
      display: `${months} month${months === 1 ? '' : 's'}`,
    };
  }

  return {
    days,
    completedYears: Math.max(0, completedYears),
    display: `${Math.max(0, completedYears)} year${completedYears === 1 ? '' : 's'}`,
  };
}

function formatReportedAge(value, unit, approximate) {
  const number = numericValue(value);
  if (number === null || number < 0) return '';

  const normalizedUnit = String(unit || 'years').toLowerCase();
  const singular = number === 1;
  const unitLabel =
    normalizedUnit === 'days'
      ? singular
        ? 'day'
        : 'days'
      : normalizedUnit === 'weeks'
        ? singular
          ? 'week'
          : 'weeks'
        : normalizedUnit === 'months'
          ? singular
            ? 'month'
            : 'months'
          : singular
            ? 'year'
            : 'years';

  return `${approximate ? 'Approximate age' : 'Reported age'}: ${number} ${unitLabel}`;
}

function responsePayload(response) {
  return response && Object.prototype.hasOwnProperty.call(response, 'data')
    ? response.data
    : response;
}

function parseCitizenFromResponse(response) {
  const payload = responsePayload(response) || {};
  const citizen =
    payload?.citizen ||
    payload?.data?.citizen ||
    payload?.data ||
    payload ||
    null;

  const citizenId = citizen?.citizenId || citizen?._id || citizen?.id || '';
  return { citizen, citizenId: String(citizenId || '') };
}

function getErrorMessage(error) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.data?.message ||
    error?.data?.error ||
    error?.message ||
    'Registration failed'
  );
}

function valuePresent(value) {
  return value !== undefined && value !== null && value !== '';
}

function buildRegistrationPayload(form) {
  const payload = {
    name: String(form.name || '').trim(),
    gender: form.gender || undefined,
    mobile: String(form.mobile || '').trim() || undefined,
    email: String(form.email || '').trim().toLowerCase() || undefined,
    address: String(form.address || '').trim() || undefined,
    aadhaar: String(form.aadhaar || '').trim() || undefined,
    occupation: String(form.occupation || '').trim() || undefined,
    centre: normalizeCentre(form.centre) || undefined,
    preferredLanguage: String(form.preferredLanguage || '').trim() || undefined,
    tobaccoSmoked: form.tobaccoSmoked || 'Never',
    tobaccoSmokeless: form.tobaccoSmokeless || 'Never',
    alcoholUse: form.alcoholUse || 'Never',
    presentingComplaint: String(form.presentingComplaint || '').trim() || undefined,
    password: form.password,
    registrationSource: form.registrationSource || 'cmfm_direct',
    profileCreatedThrough:
      form.registrationSource === 'state_hmis'
        ? 'state_hmis_referral'
        : 'doctor_portal',
  };

  if (form.ageInputMode === 'dob') {
    payload.dob = form.dob;
    payload.dateOfBirth = form.dob;
    payload.dobStatus = form.dobStatus || 'reported';
    payload.ageSource = 'date_of_birth';
    payload.ageIsApproximate = false;
  } else if (form.ageInputMode === 'reported') {
    payload.dobStatus = 'not_available';
    payload.age = numericValue(form.ageValue);
    payload.ageValue = numericValue(form.ageValue);
    payload.ageUnit = form.ageUnit || 'years';
    payload.ageAsOnDate = form.ageAsOnDate || todayDateInput();
    payload.ageSource = form.ageSource || 'self';
    payload.ageIsApproximate = Boolean(form.ageIsApproximate);
  } else {
    payload.dobStatus = 'not_available';
    payload.ageSource = 'unknown';
    payload.ageIsApproximate = true;
  }

  if (form.registrationSource === 'state_hmis') {
    payload.externalSystem = String(form.externalSystem || '').trim() || 'state_hmis';
    payload.externalPatientId = String(form.externalPatientId || '').trim() || undefined;
    payload.externalRegistrationNumber =
      String(form.externalRegistrationNumber || '').trim() || undefined;
  }

  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => valuePresent(value))
  );
}

function validateForm(form) {
  const errors = [];

  if (!String(form.name || '').trim()) errors.push('Full name is required.');
  if (!form.gender) errors.push('Gender is required.');
  if (!normalizeCentre(form.centre)) errors.push('Centre is required.');
  if (!String(form.password || '')) errors.push('Citizen portal password is required.');

  if (form.ageInputMode === 'dob') {
    const dob = parseDateInput(form.dob);
    if (!dob) errors.push('Enter a valid date of birth.');
    else if (dob.getTime() > Date.now()) errors.push('Date of birth cannot be in the future.');
  }

  if (form.ageInputMode === 'reported') {
    const ageValue = numericValue(form.ageValue);
    if (ageValue === null || ageValue < 0) {
      errors.push('Enter a valid reported age.');
    }
    if (!form.ageUnit) errors.push('Select the age unit.');
    if (!parseDateInput(form.ageAsOnDate)) errors.push('Enter a valid age-as-on date.');
  }

  if (form.registrationSource === 'state_hmis') {
    const hasExternalReference = Boolean(
      String(form.externalPatientId || '').trim() ||
        String(form.externalRegistrationNumber || '').trim()
    );
    if (!hasExternalReference) {
      errors.push(
        'For a State HMIS-linked citizen, enter the HMIS patient ID or registration number.'
      );
    }
  }

  return errors;
}

const CitizenRegistrationForm = ({ onRegistered = null }) => {
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState(createInitialForm);
  const [photo, setPhoto] = useState(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const navigate = useNavigate();

  const derivedDobAge = useMemo(
    () =>
      form.ageInputMode === 'dob'
        ? calculateAgeFromDob(form.dob, todayDateInput())
        : null,
    [form.ageInputMode, form.dob]
  );

  const reportedAgeSummary = useMemo(
    () =>
      form.ageInputMode === 'reported'
        ? formatReportedAge(form.ageValue, form.ageUnit, form.ageIsApproximate)
        : '',
    [form.ageInputMode, form.ageValue, form.ageUnit, form.ageIsApproximate]
  );

  const isHmisLinked = form.registrationSource === 'state_hmis';

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormError('');
    setForm((previous) => ({
      ...previous,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAgeModeChange = (event) => {
    const mode = event.target.value;
    setFormError('');
    setForm((previous) => ({
      ...previous,
      ageInputMode: mode,
      dob: mode === 'dob' ? previous.dob : '',
      dobStatus: mode === 'dob' ? previous.dobStatus || 'reported' : 'not_available',
      ageValue: mode === 'reported' ? previous.ageValue : '',
      ageUnit: mode === 'reported' ? previous.ageUnit || 'years' : 'years',
      ageAsOnDate:
        mode === 'reported' ? previous.ageAsOnDate || todayDateInput() : todayDateInput(),
      ageSource: mode === 'reported' ? previous.ageSource || 'self' : '',
      ageIsApproximate: mode === 'reported' ? previous.ageIsApproximate : mode === 'unknown',
    }));
  };

  const handleRegistrationSourceChange = (event) => {
    const registrationSource = event.target.value;
    setFormError('');
    setForm((previous) => ({
      ...previous,
      registrationSource,
      externalSystem:
        registrationSource === 'state_hmis'
          ? previous.externalSystem || 'state_hmis'
          : '',
      externalPatientId:
        registrationSource === 'state_hmis' ? previous.externalPatientId : '',
      externalRegistrationNumber:
        registrationSource === 'state_hmis'
          ? previous.externalRegistrationNumber
          : '',
    }));
  };

  const handlePhotoUpload = async (event) => {
    const imageFile = event.target.files?.[0];
    if (!imageFile) return;

    try {
      setFormError('');
      const options = {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 600,
        useWebWorker: true,
      };
      const compressed = await imageCompression(imageFile, options);
      const photoFile =
        compressed instanceof File
          ? compressed
          : new File([compressed], `citizen_photo_${Date.now()}.jpg`, {
              type: compressed.type || 'image/jpeg',
            });

      setForm((previous) => ({ ...previous, photo: photoFile }));
      const base64 = await imageCompression.getDataUrlFromFile(photoFile);
      setPhoto(base64);
    } catch (error) {
      setFormError(error?.message || 'Image compression failed.');
    }
  };

  const handleWebcamCapture = async (imageSource) => {
    if (!imageSource) {
      setFormError('Could not capture image. Please try again.');
      return;
    }

    try {
      setFormError('');
      const response = await fetch(imageSource);
      const blob = await response.blob();
      const options = {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 600,
        useWebWorker: true,
      };
      const compressed = await imageCompression(blob, options);
      const photoFile =
        compressed instanceof File
          ? compressed
          : new File([compressed], `citizen_photo_${Date.now()}.jpg`, {
              type: compressed.type || 'image/jpeg',
            });

      setForm((previous) => ({ ...previous, photo: photoFile }));
      const base64 = await imageCompression.getDataUrlFromFile(photoFile);
      setPhoto(base64);
      setShowWebcam(false);
    } catch (error) {
      setFormError(error?.message || 'Image compression failed.');
    }
  };

  const clearPhoto = () => {
    setPhoto(null);
    setForm((previous) => ({ ...previous, photo: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const buildFormData = (payload) => {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (!valuePresent(value)) return;
      if (typeof value === 'boolean') formData.append(key, value ? 'true' : 'false');
      else formData.append(key, String(value));
    });

    if (form.photo) {
      const photoFile =
        form.photo instanceof File
          ? form.photo
          : new File([form.photo], `citizen_photo_${Date.now()}.jpg`, {
              type: form.photo.type || 'image/jpeg',
            });
      formData.set('photo', photoFile);
    }

    return formData;
  };

  const persistSelectedCitizen = (citizen, citizenId) => {
    const { password: _registrationPassword, ...payloadFallback } =
      buildRegistrationPayload(form);
    const { password: _responsePassword, ...citizenWithoutPassword } =
      citizen || {};
    const selectedCitizen = {
      ...payloadFallback,
      ...citizenWithoutPassword,
      citizenId: String(citizenId),
      centre: normalizeCentre(citizen?.centre || form.centre),
    };

    try {
      const previousCitizen = (() => {
        try {
          return JSON.parse(localStorage.getItem('activeCitizen') || 'null') || {};
        } catch {
          return {};
        }
      })();
      const previousCitizenId = String(previousCitizen?.citizenId || '').trim();
      const previousVisitId = String(
        localStorage.getItem('activeVisitId') ||
          localStorage.getItem('visitId') ||
          ''
      ).trim();

      if (previousCitizenId) {
        localStorage.removeItem(`activeVisitId:${previousCitizenId}`);
      }
      if (previousVisitId) {
        localStorage.removeItem(`activeVisitContext:${previousVisitId}`);
      }
      localStorage.removeItem('activeVisitId');
      localStorage.removeItem('visitId');
      localStorage.removeItem('activeVisitContext');
      localStorage.removeItem('editReturnedVisit');

      localStorage.setItem('activeCitizen', JSON.stringify(selectedCitizen));
      localStorage.setItem('activeCitizenId', String(citizenId));
      localStorage.setItem('lastVisitedCitizenId', String(citizenId));
      localStorage.removeItem(`activeVisitId:${citizenId}`);

      if (selectedCitizen.centre) {
        localStorage.setItem('activeCentre', selectedCitizen.centre);
        localStorage.setItem('centre', selectedCitizen.centre);
      }
    } catch {
      // Browser-storage failure must not invalidate successful registration.
    }

    return selectedCitizen;
  };

  const resetForm = () => {
    setForm(createInitialForm());
    setPhoto(null);
    setShowWebcam(false);
    setFormError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const validationErrors = validateForm(form);
    if (validationErrors.length) {
      setFormError(validationErrors.join(' '));
      return;
    }

    try {
      setSubmitting(true);
      setFormError('');

      const payload = buildRegistrationPayload(form);
      const body = form.photo ? buildFormData(payload) : payload;
      const response = await http.post('/api/citizens/register', body);
      const { citizen, citizenId } = parseCitizenFromResponse(response);

      if (!citizenId) {
        throw new Error('Citizen registered, but Citizen ID was not returned.');
      }

      const selectedCitizen = persistSelectedCitizen(citizen, citizenId);
      const nextState = {
        citizenId: String(citizenId),
        citizen: selectedCitizen,
        selectedCitizen,
        goto: 'add-visit',
        fromCitizenRegistration: true,
      };

      window.alert('Citizen registered successfully');
      resetForm();

      if (typeof onRegistered === 'function') {
        onRegistered(nextState);
      } else {
        navigate(tenantUrl('/dashboard/operations'), {
          replace: true,
          state: nextState,
        });
      }
    } catch (error) {
      console.error('Citizen registration error:', error);
      setFormError(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full border p-2 rounded bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 disabled:opacity-60';
  const labelClass = 'block text-gray-700 dark:text-gray-200 mb-1';

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-5 max-w-2xl mx-auto bg-white dark:bg-gray-900 p-6 rounded-xl shadow"
      data-allow-submit="true"
    >
      <h2 className="text-xl font-bold text-center text-gray-900 dark:text-gray-100">
        Citizen Registration
      </h2>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100">
        Create one durable Citizen profile. Date of birth and reported age are alternative
        methods; an approximate age will not be converted into a false date of birth. State
        HMIS encounter/token details are recorded later when starting the Visit.
      </div>

      {formError && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
        >
          {formError}
        </div>
      )}

      <div>
        <label htmlFor="citizen-name" className={labelClass}>Full Name</label>
        <input
          id="citizen-name"
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="Full Name"
          className={inputClass}
          required
          disabled={submitting}
          autoComplete="name"
        />
      </div>

      <fieldset className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
        <legend className="px-2 font-semibold text-gray-900 dark:text-gray-100">
          Date of Birth / Age
        </legend>

        <div className="mb-3">
          <label htmlFor="age-input-mode" className={labelClass}>Age information available</label>
          <select
            id="age-input-mode"
            name="ageInputMode"
            value={form.ageInputMode}
            onChange={handleAgeModeChange}
            className={inputClass}
            disabled={submitting}
          >
            {AGE_INPUT_MODES.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        {form.ageInputMode === 'dob' && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="citizen-dob" className={labelClass}>Date of Birth</label>
              <input
                id="citizen-dob"
                type="date"
                name="dob"
                value={form.dob}
                onChange={handleChange}
                max={todayDateInput()}
                className={inputClass}
                required
                disabled={submitting}
              />
            </div>

            <div>
              <label htmlFor="dob-status" className={labelClass}>DOB status</label>
              <select
                id="dob-status"
                name="dobStatus"
                value={form.dobStatus}
                onChange={handleChange}
                className={inputClass}
                disabled={submitting}
              >
                <option value="reported">Reported by citizen/family</option>
                <option value="verified">Verified from available record</option>
              </select>
            </div>

            <div className="sm:col-span-2 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-200">
              {derivedDobAge
                ? `Age today: ${derivedDobAge.display}`
                : 'Select a valid date of birth to derive the age.'}
            </div>
          </div>
        )}

        {form.ageInputMode === 'reported' && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="age-value" className={labelClass}>Reported age</label>
              <input
                id="age-value"
                type="number"
                name="ageValue"
                value={form.ageValue}
                onChange={handleChange}
                min="0"
                step="0.1"
                placeholder="e.g., 18"
                className={inputClass}
                required
                disabled={submitting}
              />
            </div>

            <div>
              <label htmlFor="age-unit" className={labelClass}>Age unit</label>
              <select
                id="age-unit"
                name="ageUnit"
                value={form.ageUnit}
                onChange={handleChange}
                className={inputClass}
                required
                disabled={submitting}
              >
                {AGE_UNIT_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="age-as-on-date" className={labelClass}>Age as on date</label>
              <input
                id="age-as-on-date"
                type="date"
                name="ageAsOnDate"
                value={form.ageAsOnDate}
                onChange={handleChange}
                max={todayDateInput()}
                className={inputClass}
                required
                disabled={submitting}
              />
            </div>

            <div>
              <label htmlFor="age-source" className={labelClass}>Age source</label>
              <select
                id="age-source"
                name="ageSource"
                value={form.ageSource}
                onChange={handleChange}
                className={inputClass}
                disabled={submitting}
              >
                {AGE_SOURCE_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <label className="sm:col-span-2 flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
              <input
                type="checkbox"
                name="ageIsApproximate"
                checked={form.ageIsApproximate}
                onChange={handleChange}
                disabled={submitting}
              />
              Age is approximate / estimated
            </label>

            {reportedAgeSummary && (
              <div className="sm:col-span-2 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                {reportedAgeSummary}
              </div>
            )}
          </div>
        )}

        {form.ageInputMode === 'unknown' && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
            Age will be saved as not available. Age-specific clinical pathways will remain
            restricted until DOB or a reported age is added.
          </div>
        )}
      </fieldset>

      <div>
        <label htmlFor="citizen-gender" className={labelClass}>Gender</label>
        <select
          id="citizen-gender"
          name="gender"
          value={form.gender}
          onChange={handleChange}
          className={inputClass}
          required
          disabled={submitting}
        >
          <option value="">Select Gender</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Transgender">Transgender</option>
          <option value="Non-binary">Non-binary</option>
          <option value="Other">Other</option>
          <option value="Prefer not to say">Prefer not to say</option>
        </select>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="citizen-mobile" className={labelClass}>Mobile (optional)</label>
          <input
            id="citizen-mobile"
            type="tel"
            name="mobile"
            value={form.mobile}
            onChange={handleChange}
            placeholder="Mobile"
            className={inputClass}
            disabled={submitting}
            autoComplete="tel"
          />
        </div>

        <div>
          <label htmlFor="citizen-email" className={labelClass}>Email (optional)</label>
          <input
            id="citizen-email"
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="Email"
            className={inputClass}
            disabled={submitting}
            autoComplete="email"
          />
        </div>
      </div>

      <div>
        <label htmlFor="citizen-address" className={labelClass}>Address</label>
        <textarea
          id="citizen-address"
          name="address"
          value={form.address}
          onChange={handleChange}
          placeholder="Address"
          className={inputClass}
          rows={2}
          disabled={submitting}
          autoComplete="street-address"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="citizen-aadhaar" className={labelClass}>Aadhaar (optional)</label>
          <input
            id="citizen-aadhaar"
            type="text"
            name="aadhaar"
            value={form.aadhaar}
            onChange={handleChange}
            placeholder="Aadhaar"
            className={inputClass}
            disabled={submitting}
            inputMode="numeric"
          />
        </div>

        <div>
          <label htmlFor="citizen-occupation" className={labelClass}>Occupation</label>
          <input
            id="citizen-occupation"
            type="text"
            name="occupation"
            value={form.occupation}
            onChange={handleChange}
            placeholder="Occupation"
            className={inputClass}
            disabled={submitting}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="citizen-centre" className={labelClass}>Centre</label>
          <select
            id="citizen-centre"
            name="centre"
            value={form.centre}
            onChange={handleChange}
            className={inputClass}
            required
            disabled={submitting}
          >
            <option value="">Select Centre</option>
            {CENTRE_OPTIONS.map((centre) => (
              <option key={centre} value={centre}>{centre}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="preferred-language" className={labelClass}>Preferred language</label>
          <input
            id="preferred-language"
            type="text"
            name="preferredLanguage"
            value={form.preferredLanguage}
            onChange={handleChange}
            placeholder="e.g., Hindi / English / Marwari"
            className={inputClass}
            disabled={submitting}
          />
        </div>
      </div>

      <fieldset className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
        <legend className="px-2 font-semibold text-gray-900 dark:text-gray-100">
          Registration Source
        </legend>

        <div>
          <label htmlFor="registration-source" className={labelClass}>Citizen profile source</label>
          <select
            id="registration-source"
            name="registrationSource"
            value={form.registrationSource}
            onChange={handleRegistrationSourceChange}
            className={inputClass}
            disabled={submitting}
          >
            {REGISTRATION_SOURCE_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        {isHmisLinked && (
          <div className="mt-3 space-y-3">
            <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100">
              Store only patient-level HMIS identifiers here. The HMIS Visit/encounter ID,
              token, referral reason and referring person are recorded when starting the Visit.
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="external-system" className={labelClass}>External system</label>
                <input
                  id="external-system"
                  type="text"
                  name="externalSystem"
                  value={form.externalSystem}
                  onChange={handleChange}
                  placeholder="state_hmis"
                  className={inputClass}
                  disabled={submitting}
                />
              </div>

              <div>
                <label htmlFor="external-patient-id" className={labelClass}>HMIS Patient ID</label>
                <input
                  id="external-patient-id"
                  type="text"
                  name="externalPatientId"
                  value={form.externalPatientId}
                  onChange={handleChange}
                  placeholder="HMIS Patient ID"
                  className={inputClass}
                  disabled={submitting}
                />
              </div>
            </div>

            <div>
              <label htmlFor="external-registration-number" className={labelClass}>
                HMIS Registration Number
              </label>
              <input
                id="external-registration-number"
                type="text"
                name="externalRegistrationNumber"
                value={form.externalRegistrationNumber}
                onChange={handleChange}
                placeholder="HMIS Registration Number"
                className={inputClass}
                disabled={submitting}
              />
            </div>
          </div>
        )}
      </fieldset>

      <fieldset className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
        <legend className="px-2 font-semibold text-gray-900 dark:text-gray-100">
          Lifestyle Profile
        </legend>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label htmlFor="tobacco-smoked" className={labelClass}>Smoking</label>
            <select
              id="tobacco-smoked"
              name="tobaccoSmoked"
              value={form.tobaccoSmoked}
              onChange={handleChange}
              className={inputClass}
              disabled={submitting}
            >
              <option value="Never">Never</option>
              <option value="Past">Past</option>
              <option value="Current">Current</option>
            </select>
          </div>

          <div>
            <label htmlFor="tobacco-smokeless" className={labelClass}>Smokeless tobacco</label>
            <select
              id="tobacco-smokeless"
              name="tobaccoSmokeless"
              value={form.tobaccoSmokeless}
              onChange={handleChange}
              className={inputClass}
              disabled={submitting}
            >
              <option value="Never">Never</option>
              <option value="Past">Past</option>
              <option value="Current">Current</option>
            </select>
          </div>

          <div>
            <label htmlFor="alcohol-use" className={labelClass}>Alcohol</label>
            <select
              id="alcohol-use"
              name="alcoholUse"
              value={form.alcoholUse}
              onChange={handleChange}
              className={inputClass}
              disabled={submitting}
            >
              <option value="Never">Never</option>
              <option value="Past">Past</option>
              <option value="Current">Current</option>
            </select>
          </div>
        </div>
      </fieldset>

      <div>
        <label htmlFor="presenting-complaint" className={labelClass}>
          Presenting Complaint (optional profile note)
        </label>
        <textarea
          id="presenting-complaint"
          name="presentingComplaint"
          value={form.presentingComplaint}
          onChange={handleChange}
          placeholder="Presenting complaint"
          className={inputClass}
          rows={2}
          disabled={submitting}
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          The definitive complaint and clinical context are recorded in Add Visit.
        </p>
      </div>

      <div>
        <label className={labelClass}>Photo (optional)</label>
        <div className="flex items-center gap-3 flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handlePhotoUpload}
            className="block text-sm text-gray-700 dark:text-gray-200"
            disabled={submitting}
          />

          <button
            type="button"
            onClick={() => setShowWebcam((current) => !current)}
            className="px-3 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-60"
            disabled={submitting}
          >
            {showWebcam ? 'Close Camera' : 'Capture via Camera'}
          </button>

          {photo && (
            <button
              type="button"
              onClick={clearPhoto}
              className="px-3 py-2 rounded border border-gray-300 text-gray-800 dark:border-gray-600 dark:text-gray-100 disabled:opacity-60"
              disabled={submitting}
            >
              Remove Photo
            </button>
          )}
        </div>

        {showWebcam && (
          <div className="mt-3 space-y-2">
            <Webcam
              ref={webcamRef}
              audio={false}
              screenshotFormat="image/jpeg"
              videoConstraints={{ facingMode: 'user' }}
              className="rounded w-full"
            />

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleWebcamCapture(webcamRef.current?.getScreenshot?.())}
                className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                disabled={submitting}
              >
                Capture Photo
              </button>

              <button
                type="button"
                onClick={() => setShowWebcam(false)}
                className="px-3 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-60"
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {photo && (
          <img
            src={photo}
            alt="Citizen preview"
            className="w-24 h-24 object-cover mt-3 rounded border border-gray-200 dark:border-gray-700"
          />
        )}
      </div>

      <div>
        <label htmlFor="citizen-password" className={labelClass}>Citizen Portal Password</label>
        <input
          id="citizen-password"
          type="password"
          name="password"
          value={form.password}
          onChange={handleChange}
          placeholder="Set Password"
          className={inputClass}
          required
          disabled={submitting}
          autoComplete="new-password"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Password remains required by the current Citizen account model and login workflow.
        </p>
      </div>

      <button
        type="submit"
        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed w-full mt-2"
        disabled={submitting}
      >
        {submitting ? 'Registering…' : 'Register Citizen'}
      </button>
    </form>
  );
};

export default CitizenRegistrationForm;
